import { addressScheme } from '@app/formats'
import { createElement, type ReactNode, useMemo } from 'react'
import { HEBREW, RIGHT_TO_LEFT, STRONG } from './formats/reading-direction'
import './rich-text-view.css'

/** The elements, attributes and addresses a body may hold. */
export type HtmlAllowlist = {
  readonly tags: readonly string[]
  readonly attributes: Readonly<Record<string, readonly string[]>>
  readonly linkSchemes: readonly string[]
  readonly imageSchemes?: readonly string[]
}

/**
 * Simple HTML, read, and set by the stylesheet of the surface it sits on.
 *
 * The body is parsed and rebuilt as elements through the allowlist the server
 * stored it through, so an element, an attribute or an address the list does
 * not name is never drawn, whatever reached the stored text. A web link opens
 * in a tab of its own; a link or an image without an address the list admits
 * is drawn as its words, or not at all.
 *
 * Each block reads in a direction of its own, taken from its first letter,
 * and a block written in Hebrew letters takes the Hebrew face. A list takes
 * the direction of the first letter inside it, so its markers sit on the side
 * its items begin. A table scrolls sideways in its own region, never the page.
 */
export function RichTextView({
  html,
  allow,
}: {
  html: string
  allow: HtmlAllowlist
}) {
  const drawn = useMemo(() => draw(html, allow), [html, allow])
  return <div className="rich-text-view">{drawn}</div>
}

const HOLDS_WORDS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'li',
  'th',
  'td',
  'blockquote',
])
const HOLDS_BLOCKS = new Set(['ul', 'ol', 'table'])
/** Where a browser draws no text, so the spaces between tags are not words. */
const HOLDS_ROWS = new Set(['table', 'thead', 'tbody', 'tr'])
const EMPTY = new Set(['br', 'hr', 'img'])
const ALIGN = /^\s*text-align\s*:\s*(left|center|right)\s*;?\s*$/i

function draw(html: string, allow: HtmlAllowlist): ReactNode[] {
  const tags = new Set(allow.tags)
  const linkSchemes = new Set(allow.linkSchemes)
  const imageSchemes = new Set(allow.imageSchemes ?? [])
  const named = (tag: string, attr: string) =>
    (allow.attributes[tag] ?? []).includes(attr) ||
    (allow.attributes['*'] ?? []).includes(attr)
  const doc = new DOMParser().parseFromString(html, 'text/html')

  const children = (node: Node, path: string): ReactNode[] =>
    [...node.childNodes].flatMap((child, i) => one(child, `${path}.${i}`))

  const one = (node: Node, key: string): ReactNode[] => {
    if (node.nodeType === Node.TEXT_NODE) {
      const inRows = HOLDS_ROWS.has(
        node.parentElement?.tagName.toLowerCase() ?? '',
      )
      return inRows && !node.textContent?.trim() ? [] : [node.textContent ?? '']
    }
    if (!(node instanceof Element)) return []
    const tag = node.tagName.toLowerCase()
    /* An element the list does not name is dropped, and its words kept. */
    if (!tags.has(tag)) return children(node, key)
    const props: Record<string, unknown> = { key }
    /* Stated from the first letter rather than left to `dir="auto"`, which
       passes over a block inside that states its own — so a quotation of
       Hebrew paragraphs would otherwise read left to right. */
    const first = STRONG.exec(node.textContent ?? '')?.[0] ?? ''
    if (HOLDS_WORDS.has(tag))
      props.dir = !first ? 'auto' : RIGHT_TO_LEFT.test(first) ? 'rtl' : 'ltr'
    if (HOLDS_BLOCKS.has(tag) && RIGHT_TO_LEFT.test(first)) props.dir = 'rtl'
    if (props.dir === 'rtl' && HEBREW.test(first))
      props['data-script'] = 'hebrew'
    const dir = node.getAttribute('dir')
    if (named(tag, 'dir') && (dir === 'rtl' || dir === 'ltr')) props.dir = dir
    if (named(tag, 'style')) {
      const align = ALIGN.exec(node.getAttribute('style') ?? '')?.[1]
      if (align) props.style = { textAlign: align }
    }
    if (tag === 'ol' && named(tag, 'start')) {
      const start = Number(node.getAttribute('start'))
      if (Number.isInteger(start) && start > 0) props.start = start
    }
    if (tag === 'a') {
      const href = node.getAttribute('href')?.trim() ?? ''
      const scheme = addressScheme(href)
      if (!scheme || !linkSchemes.has(scheme)) return children(node, key)
      props.href = href
      if (scheme === 'https' || scheme === 'http') {
        props.target = '_blank'
        props.rel = 'noopener noreferrer'
      }
    }
    if (tag === 'img') {
      const src = node.getAttribute('src')?.trim() ?? ''
      if (!imageSchemes.has(addressScheme(src) ?? '')) return []
      props.src = src
      props.alt = node.getAttribute('alt') ?? ''
      props.loading = 'lazy'
      props.referrerPolicy = 'no-referrer'
    }
    if (EMPTY.has(tag)) return [createElement(tag, props)]
    const element = createElement(tag, props, ...children(node, key))
    /* The region a wide table scrolls in reads the table's way, so a Hebrew
       table opens on its first column. */
    return tag === 'table'
      ? [
          <div
            key={key}
            className="rich-text-view__table"
            dir={typeof props.dir === 'string' ? props.dir : undefined}
          >
            {element}
          </div>,
        ]
      : [element]
  }

  return children(doc.body, 'r')
}
